import type { Virtualizer } from '@tanstack/react-virtual';
import { useCallback } from 'react';
import { CardVirtualizationContext, useSwimlaneRootProps, useSwimlaneState } from '../context';

import { SwimlaneGrid } from './body/SwimlaneGrid';
import { CARD_WIDTH, COMPACT_CARD_WIDTH, GRID_GAP } from './constants';
import { CardsDNDContextProvider } from './dnd';
import { AllItemsFiltered } from './empty-state/AllItemsFiltered';
import { NoItems } from './empty-state/NoItems';
import { NoSubItems } from './empty-state/NoSubItems';
import { ColumnHeaders } from './headers/columns/ColumnHeaders';
import { RowHeaders } from './headers/rows/RowHeaders';

interface IExportLayoutRoot {
  scrollContainer: HTMLDivElement;
}

/**
 * Non-virtualized layout for PNG export.
 * Renders all grid items at once to avoid scroll position resets during tiled screenshot capture.
 * The export service would otherwise experience issues with virtualization dynamically
 * mounting/unmounting DOM elements as it scrolls, causing scroll positions to reset unpredictably.
 */
export function ExportLayoutRoot({ scrollContainer }: IExportLayoutRoot) {
  const {
    rowHeaders,
    columnHeaders,
    isCompact,
    isItemDateVisible,
    isItemDescriptionVisible,
    isItemDetailsVisible,
    emptyState,
  } = useSwimlaneRootProps();
  const { getRowHeight } = useSwimlaneState();

  const getColumnItemKey = useCallback(
    (index: number) => {
      const flags = [isCompact, isItemDateVisible, isItemDescriptionVisible, isItemDetailsVisible]
        .map(String)
        .join('-');
      return `${columnHeaders[index]?.id}${flags}` || index.toString();
    },
    [columnHeaders, isCompact, isItemDateVisible, isItemDescriptionVisible, isItemDetailsVisible],
  );

  const getRowItemKey = useCallback(
    (index: number) => {
      const flags = [isCompact, isItemDateVisible, isItemDescriptionVisible, isItemDetailsVisible]
        .map(String)
        .join('-');
      return `${rowHeaders[index]?.id}${flags}` || index.toString();
    },
    [rowHeaders, isCompact, isItemDateVisible, isItemDescriptionVisible, isItemDetailsVisible],
  );

  let columnOffset = 0;
  const allColumnItems = columnHeaders.map((_header, index) => {
    const size = isCompact ? COMPACT_CARD_WIDTH.value : CARD_WIDTH.value;
    const item = {
      index,
      key: getColumnItemKey(index),
      start: columnOffset,
      size,
      end: columnOffset + size,
      lane: 0,
    };
    columnOffset += size + GRID_GAP.value;
    return item;
  });

  let rowOffset = 0;
  const allRowItems = rowHeaders.map((_header, index) => {
    const rowID = rowHeaders[index]?.id ?? 'null';
    const size = getRowHeight(rowID);
    const item = {
      index,
      key: getRowItemKey(index),
      start: rowOffset,
      size,
      end: rowOffset + size,
      lane: 0,
    };
    rowOffset += size + GRID_GAP.value;
    return item;
  });

  const totalColumnWidth =
    allColumnItems.length > 0 ? (allColumnItems[allColumnItems.length - 1]?.end ?? 0) : 0;
  const totalRowHeight =
    allRowItems.length > 0 ? (allRowItems[allRowItems.length - 1]?.end ?? 0) : 0;

  const columnVirtualizer = {
    getVirtualItems: () => allColumnItems,
    getTotalSize: () => totalColumnWidth,
    scrollToIndex: () => {},
    scrollToOffset: () => {},
    measure: () => {},
    resizeItem: () => {},
  } as unknown as Virtualizer<HTMLDivElement, Element>;

  const rowVirtualizer = {
    getVirtualItems: () => allRowItems,
    getTotalSize: () => totalRowHeight,
    scrollToIndex: () => {},
    scrollToOffset: () => {},
    measure: () => {},
    resizeItem: () => {},
    measureElement: () => {},
    scrollElement: scrollContainer,
    scrollOffset: 0,
    options: {
      scrollMargin: 0,
      estimateSize: (index: number) => getRowHeight(rowHeaders[index]?.id ?? 'null'),
    },
  } as unknown as Virtualizer<HTMLDivElement, Element>;

  return (
    <>
      <ColumnHeaders columnVirtualizer={columnVirtualizer} />
      <RowHeaders rowVirtualizer={rowVirtualizer} />
      {/* CardsDNDContextProvider is required even though drag-and-drop is inactive during export.
          SortableCard and SwimlaneGridCell unconditionally call useSortable/useDroppable, which
          require a DndContext ancestor to be present in the tree. */}
      <CardsDNDContextProvider onDragEnd={() => {}}>
        {emptyState === 'no-items' && <NoItems />}
        {emptyState === 'filtered' && <AllItemsFiltered />}
        {emptyState === 'no-sub-items' && <NoSubItems />}
        {emptyState === 'no-sub-items-portfolio' && <NoSubItems isMaster />}
        {!emptyState && (
          <CardVirtualizationContext.Provider value={rowVirtualizer}>
            <SwimlaneGrid columnVirtualizer={columnVirtualizer} rowVirtualizer={rowVirtualizer} />
          </CardVirtualizationContext.Provider>
        )}
      </CardsDNDContextProvider>
    </>
  );
}
